import dayjs from 'dayjs';

const compactFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const numberFormatter = new Intl.NumberFormat('en-US');

export const formatCompactNumber = (value: number): string => {
  return compactFormatter.format(value);
};

export const formatNumber = (value: number): string => {
  return numberFormatter.format(value);
};

export const formatCurrency = (value: number): string => {
  return `$${numberFormatter.format(value)}`;
};

/**
 * The em-dash placeholder for a value we do not have.
 *
 * Reserved for *unknown* — no goal set, no benchmark configured, the source
 * unreachable. A value that is genuinely zero renders `0`, because "—" and "0"
 * answer different questions and a reader cannot tell them apart otherwise
 * (NPJB-465 AC16).
 *
 * Previously declared locally in eight components, which is how they drifted.
 */
export const DASH = '\u2014';

/**
 * Render a percentage that is already in percent units (`0.18` -> `"0.18%"`).
 *
 * Takes a value **already in percent units** — `0.18` renders `"0.18%"`. The
 * click benchmark is a *ratio* (`0.10` = 10%), so callers showing it must
 * multiply by 100 first; `ClicksCtrCard` does. Returns {@link DASH} for a
 * missing value rather than hiding the field, so the row keeps its shape.
 */
export const formatPercent = (value: number | null | undefined, digits = 2): string =>
  value === null || value === undefined ? DASH : `${value.toFixed(digits)}%`;

/** Format a count, or {@link DASH} when it is unknown. A real `0` renders `0`. */
export const formatNumberOrDash = (value: number | null | undefined): string =>
  value === null || value === undefined ? DASH : numberFormatter.format(value);

export const pluralize = (count: number, singular: string, plural: string): string =>
  count === 1 ? singular : plural;

export const formatUploadDate = (date: string | null | undefined): string => {
  if (!date || !dayjs(date).isValid()) return 'NA';
  return dayjs(date).format('MMM D, YYYY');
};

const FULL_DATE = 'MMM D, YYYY';

/** NPJB-821 — the one date-and-time format: "Oct 21, 2026 · 3:05 PM". */
const DATE_TIME = 'MMM D, YYYY · h:mm A';

/**
 * Format a timestamp as {@link DATE_TIME}, or {@link DASH} when it is missing or invalid.
 * dayjs rolls a bad date like `2026-99-99` forward into a real one, so the native `Date`
 * check rejects it first.
 */
export const formatDateTime = (value: string | Date | null | undefined): string => {
  if (!value || Number.isNaN(new Date(value).getTime())) return DASH;
  return dayjs(value).format(DATE_TIME);
};

/**
 * The design's employee-range labels: `1001 to 5000` → `1,001–5,000`, `10001+` → `10,001+`.
 * A value that is not a range keeps its label. The dash is an en dash.
 */
export const employeeRangeLabel = (label: string): string => {
  const range = /^(\d+)\s+to\s+(\d+)$/.exec(label.trim());
  if (range) return `${formatNumber(Number(range[1]))}–${formatNumber(Number(range[2]))}`;
  const open = /^(\d+)\+$/.exec(label.trim());
  if (open) return `${formatNumber(Number(open[1]))}+`;
  return label;
};

/**
 * NPJB-552 — render a campaign timeline for read-only surfaces.
 *
 * The two dates are stored and transported independently, so every combination
 * is reachable: both, either one alone, or neither. Same-year ranges drop the
 * redundant first year ("Jan 15 – Jul 15, 2026"); cross-year ranges keep both.
 * Returns null when there is nothing to show, so callers can decide between an
 * em-dash placeholder and hiding the field entirely.
 */
export const formatDateRange = (
  start: string | null | undefined,
  end: string | null | undefined,
): string | null => {
  const from = start && dayjs(start).isValid() ? dayjs(start) : null;
  const to = end && dayjs(end).isValid() ? dayjs(end) : null;

  if (from && to) {
    const startFormat = from.isSame(to, 'year') ? 'MMM D' : FULL_DATE;
    return `${from.format(startFormat)} – ${to.format(FULL_DATE)}`;
  }
  if (from) return from.format(FULL_DATE);
  if (to) return to.format(FULL_DATE);
  return null;
};
